import { useState } from 'react';
import PropTypes from 'prop-types';
import { Avatar, Spinner } from '@components/ui';
import { useProjectActivity, useEntityActivity } from '@hooks';
import { STATUS_CONFIG } from '@config/constants';

const INITIAL_DISPLAY_COUNT = 5;

const actionIcons = {
  created: '✨',
  updated: '✏️',
  deleted: '🗑️',
  status_changed: '🔄',
  assigned: '👤',
  unassigned: '👤',
  commented: '💬',
  started: '▶️',
  completed: '✅',
  time_logged: '⏱️',
  member_added: '➕',
  member_removed: '➖',
};

const actionLabels = {
  created: 'creó',
  updated: 'actualizó',
  deleted: 'eliminó',
  status_changed: 'cambió el estado de',
  assigned: 'asignó',
  unassigned: 'desasignó',
  commented: 'comentó en',
  started: 'inició',
  completed: 'completó',
  time_logged: 'registró tiempo en',
  member_added: 'agregó miembro a',
  member_removed: 'removió miembro de',
};

const entityLabels = {
  task: 'la tarea',
  sprint: 'el sprint',
  epic: 'la epic',
  project: 'el proyecto',
  milestone: 'el hito',
  comment: 'el comentario',
};

export default function ActivityFeed({ projectId, entity, entityId, limit = 20, compact = false }) {
  const [showAll, setShowAll] = useState(false);

  // Use the appropriate hook based on props
  const projectActivityQuery = useProjectActivity(
    projectId,
    { limit },
    { enabled: !!projectId && !entity, refetchOnMount: 'always', staleTime: 0 }
  );

  const entityActivityQuery = useEntityActivity(
    entity,
    entityId,
    { limit },
    { enabled: !!entity && !!entityId, refetchOnMount: 'always', staleTime: 0 }
  );

  const { data, isLoading } = projectId && !entity ? projectActivityQuery : entityActivityQuery;

  const allActivities = data?.data || [];
  const activities = showAll ? allActivities : allActivities.slice(0, INITIAL_DISPLAY_COUNT);
  const hasMore = allActivities.length > INITIAL_DISPLAY_COUNT;

  const formatDate = (date) => {
    const d = new Date(date);
    const now = new Date();
    const diffMs = now - d;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Ahora';
    if (diffMins < 60) return `Hace ${diffMins}m`;
    if (diffHours < 24) return `Hace ${diffHours}h`;
    if (diffDays < 7) return `Hace ${diffDays}d`;

    return d.toLocaleDateString('es-ES', {
      day: 'numeric',
      month: 'short',
    });
  };

  const getInitials = (name) => {
    return name
      ?.split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase() || '?';
  };

  // Translate status values to readable labels
  const getStatusLabel = (status) => {
    return STATUS_CONFIG[status]?.label || status;
  };

  // Translate field names to Spanish
  const fieldLabels = {
    status: 'Estado',
    title: 'Título',
    priority: 'Prioridad',
    type: 'Tipo',
  };

  const renderChanges = (changes) => {
    if (!changes || typeof changes !== 'object') return null;

    const entries = Object.entries(changes);
    if (entries.length === 0) return null;

    return (
      <div
        style={{
          marginTop: '8px',
          padding: '8px 12px',
          backgroundColor: '#f8fafc',
          borderRadius: '8px',
          fontSize: '12px',
        }}
      >
        {entries.map(([key, value]) => {
          if (typeof value === 'object' && value.from !== undefined && value.to !== undefined) {
            // Translate values if it's a status field
            const fromValue = key === 'status' ? getStatusLabel(value.from) : value.from;
            const toValue = key === 'status' ? getStatusLabel(value.to) : value.to;
            const fieldLabel = fieldLabels[key] || key;

            return (
              <div key={key} style={{ color: '#64748b', marginBottom: '4px' }}>
                <span style={{ color: '#94a3b8' }}>{fieldLabel}:</span>{' '}
                <span style={{ textDecoration: 'line-through', color: '#ef4444' }}>{fromValue}</span>
                {' → '}
                <span style={{ color: '#10b981' }}>{toValue}</span>
              </div>
            );
          }
          return null;
        })}
      </div>
    );
  };

  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '24px' }}>
        <Spinner />
      </div>
    );
  }

  if (activities.length === 0) {
    return (
      <div
        style={{
          padding: compact ? '16px' : '32px',
          textAlign: 'center',
          backgroundColor: '#f8fafc',
          borderRadius: '8px',
          border: '1px dashed #e2e8f0',
        }}
      >
        <p style={{ fontSize: compact ? '12px' : '14px', color: '#94a3b8', margin: 0 }}>
          No hay actividad registrada
        </p>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div style={{ marginBottom: compact ? '10px' : '16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <h4 style={{ fontSize: compact ? '12px' : '14px', fontWeight: '600', color: '#374151', margin: 0, textTransform: compact ? 'uppercase' : 'none', letterSpacing: compact ? '0.05em' : 'normal' }}>
          Actividad Reciente
        </h4>
        {compact && allActivities.length > 0 && (
          <span style={{ fontSize: '11px', color: '#9ca3af', backgroundColor: '#f3f4f6', padding: '2px 8px', borderRadius: '10px' }}>
            {allActivities.length}
          </span>
        )}
      </div>

      {/* Activity timeline - con scroll en modo compacto */}
      <div style={{
        position: 'relative',
        maxHeight: compact ? '280px' : 'none',
        overflowY: compact ? 'auto' : 'visible',
        paddingRight: compact ? '4px' : '0',
      }}>
        {/* Timeline line */}
        {!compact && (
          <div
            style={{
              position: 'absolute',
              left: '15px',
              top: '20px',
              bottom: '20px',
              width: '2px',
              backgroundColor: '#e5e7eb',
            }}
          />
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: compact ? '8px' : '14px' }}>
          {activities.map((activity) => (
            <div
              key={activity.id}
              style={{
                display: 'flex',
                gap: compact ? '10px' : '12px',
                position: 'relative',
                padding: compact ? '8px' : '0',
                backgroundColor: compact ? '#fafafa' : 'transparent',
                borderRadius: compact ? '8px' : '0',
                border: compact ? '1px solid #f3f4f6' : 'none',
              }}
            >
              {/* Icon/Avatar - más pequeño en modo compacto */}
              <div
                style={{
                  position: 'relative',
                  zIndex: 1,
                  backgroundColor: compact ? 'transparent' : 'white',
                  padding: compact ? '0' : '2px',
                  flexShrink: 0,
                }}
              >
                {compact ? (
                  <div style={{
                    width: '28px',
                    height: '28px',
                    borderRadius: '50%',
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white',
                    fontSize: '10px',
                    fontWeight: '600',
                  }}>
                    {getInitials(activity.user?.name)}
                  </div>
                ) : (
                  <Avatar initials={getInitials(activity.user?.name)} size="sm" />
                )}
              </div>

              {/* Content */}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', flexDirection: compact ? 'column' : 'row', alignItems: compact ? 'flex-start' : 'flex-start', gap: compact ? '2px' : '6px', flexWrap: 'wrap' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                    <span style={{ fontSize: compact ? '12px' : '13px', fontWeight: '600', color: '#1f2937' }}>
                      {activity.user?.name || 'Usuario'}
                    </span>
                    <span style={{ fontSize: compact ? '11px' : '13px', color: '#6b7280' }}>
                      {actionIcons[activity.action] || '📝'}{' '}
                      {actionLabels[activity.action] || activity.action}
                    </span>
                  </div>
                  <span style={{ fontSize: '10px', color: '#9ca3af', marginLeft: compact ? '0' : 'auto' }}>
                    {formatDate(activity.createdAt)}
                  </span>
                </div>

                {/* Show changes if any - más compacto */}
                {activity.changes && Object.keys(activity.changes).length > 0 && (
                  <div
                    style={{
                      marginTop: '6px',
                      padding: compact ? '6px 8px' : '8px 12px',
                      backgroundColor: '#f9fafb',
                      borderRadius: '6px',
                      fontSize: compact ? '11px' : '12px',
                    }}
                  >
                    {Object.entries(activity.changes).map(([key, value]) => {
                      if (typeof value === 'object' && value.from !== undefined && value.to !== undefined) {
                        const fromValue = key === 'status' ? getStatusLabel(value.from) : value.from;
                        const toValue = key === 'status' ? getStatusLabel(value.to) : value.to;
                        const fieldLabel = fieldLabels[key] || key;

                        return (
                          <div key={key} style={{ color: '#6b7280', display: 'flex', alignItems: 'center', gap: '4px', flexWrap: 'wrap' }}>
                            <span style={{ color: '#9ca3af' }}>{fieldLabel}:</span>
                            <span style={{ textDecoration: 'line-through', color: '#ef4444', fontSize: '10px' }}>{fromValue}</span>
                            <span style={{ color: '#9ca3af' }}>→</span>
                            <span style={{ color: '#10b981', fontWeight: '500' }}>{toValue}</span>
                          </div>
                        );
                      }
                      return null;
                    })}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Show more/less button */}
        {hasMore && (
          <button
            onClick={() => setShowAll(!showAll)}
            style={{
              display: 'block',
              width: '100%',
              marginTop: compact ? '8px' : '12px',
              padding: compact ? '6px' : '8px',
              backgroundColor: 'transparent',
              border: '1px solid #e5e7eb',
              borderRadius: '6px',
              color: '#6366f1',
              fontSize: compact ? '11px' : '13px',
              fontWeight: '500',
              cursor: 'pointer',
              transition: 'all 0.15s',
            }}
            onMouseOver={(e) => {
              e.target.style.backgroundColor = '#f9fafb';
            }}
            onMouseOut={(e) => {
              e.target.style.backgroundColor = 'transparent';
            }}
          >
            {showAll ? 'Mostrar menos' : `Mostrar más (${allActivities.length - INITIAL_DISPLAY_COUNT} más)`}
          </button>
        )}
      </div>
    </div>
  );
}

ActivityFeed.propTypes = {
  projectId: PropTypes.number,
  entity: PropTypes.string,
  entityId: PropTypes.number,
  limit: PropTypes.number,
  compact: PropTypes.bool,
};
